import { Loader2 } from 'lucide-react';
import { useState } from 'react';

import { DocumentSection } from '../DocumentSection';
import { useCancelProject } from '../../hooks/useCancelProject';
import { Button } from '@/shared/components/ui/button';
import { Sheet } from '@/shared/components/ui/sheet';
import { Textarea } from '@/shared/components/ui/textarea';
import { messages } from '@/shared/constants/messages';
import { toUserMessage } from '@/shared/lib/errors';
import type { ProjectWithContext } from '@/shared/types/domain';

/**
 * As arestas para `cancelled` em `project_transitions` (spec §4.4) — cópia de
 * leitura só para decidir se o BOTÃO aparece; a RPC é quem recusa de
 * verdade se este mapa ficar desatualizado.
 */
const CANCELLABLE_STATUSES = new Set(['under_review', 'changes_requested', 'approved']);

interface CloseProjectSectionProps {
  project: ProjectWithContext;
  /** Convite de UX: `can_write_ministry(...) || can_decide_for_ministry(...)`. */
  canAct: boolean;
}

/**
 * "Cancelar projeto", fora do painel de decisão (spec §6.4) — colar "Cancelar"
 * ao lado de "Rejeitar" faria o revisor errar o verbo, e os dois são
 * irreversíveis. `cancel_project` é dos dois lados: líder OU quem decide.
 *
 * ⚠️ **"Marcar como concluído" SAIU** (decisão 29), e não pode voltar. Ninguém
 * declara que um evento aconteceu — a data declara, e "Aguardando relatório" é
 * essa leitura (`ProjectStatusBadge`). Nada vira `completed` na v1, de
 * propósito: a pilha que se forma é a pressão que justifica o relatório
 * pós-projeto na v1.1, e como o estado é derivado, o dia em que `submit_report`
 * existir todos fecham sozinhos. `complete_project` fica no banco, sem
 * chamador.
 */
export function CloseProjectSection({ project, canAct }: CloseProjectSectionProps) {
  const canCancel = canAct && CANCELLABLE_STATUSES.has(project.status);

  if (!canCancel) return null;

  return (
    <DocumentSection
      id="cancelar-projeto"
      title={messages.review.cancelProject}
      description={messages.review.cancelSectionHint}
    >
      <CancelProjectButton project={project} />
    </DocumentSection>
  );
}

function CancelProjectButton({ project }: { project: ProjectWithContext }) {
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [comment, setComment] = useState('');
  const cancel = useCancelProject();

  function handleClose() {
    if (cancel.isPending) return;
    setIsConfirmOpen(false);
  }

  return (
    <>
      <Button
        type="button"
        variant="ghost"
        className="text-muted-foreground hover:bg-destructive/10 hover:text-destructive focus-visible:text-destructive"
        onClick={() => setIsConfirmOpen(true)}
      >
        {messages.review.cancelProject}
      </Button>

      <Sheet
        open={isConfirmOpen}
        onClose={handleClose}
        placement="center"
        dismissible={!cancel.isPending}
        title={messages.review.cancelConfirmTitle}
        description={messages.review.cancelConfirmDescription}
        footer={
          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={cancel.isPending}
            >
              {messages.actions.back}
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={cancel.isPending || comment.trim().length === 0}
              onClick={() =>
                cancel.mutate(
                  { projectId: project.id, ministryId: project.ministry_id, comment: comment.trim() },
                  { onSuccess: () => setIsConfirmOpen(false) },
                )
              }
            >
              {cancel.isPending ? (
                <Loader2 aria-hidden="true" className="size-4 animate-spin" />
              ) : null}
              {cancel.isPending ? messages.review.cancelling : messages.review.cancelProject}
            </Button>
          </div>
        }
      >
        <div className="space-y-1.5">
          <label htmlFor="cancel-comment" className="text-sm font-medium">
            Motivo
          </label>
          <Textarea
            id="cancel-comment"
            value={comment}
            onChange={(event) => setComment(event.target.value)}
            rows={4}
            disabled={cancel.isPending}
            autoFocus
          />
        </div>

        {cancel.error ? (
          <p
            role="alert"
            className="animate-reveal mt-3 rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm text-destructive"
          >
            {toUserMessage(cancel.error)}
          </p>
        ) : null}
      </Sheet>
    </>
  );
}
